import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import PageForm from 'components/pageForm/PageForm';
import Pagination from 'components/pagination/pagination';
import ReviewTable from 'components/reviews/ReviewTable';
import ReviewsToolbar from 'components/reviews/ReviewsToolbar';
import useModal from 'components/modal/useModal';
import useSlideOver from 'components/SlideOver/useSlideOver';
import UseContext from 'hooks/useContext';
import useLeavingItems from 'hooks/useLeavingItems';
import useText from 'hooks/useText';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ApiDatabase from 'server';

const REVIEWS_PER_PAGE = 25;
const PATHS = {
  untreated: '/vivier-rating-temp-worker/rating-untreated',
  treated: '/vivier-rating-temp-worker/rating-treated',
};

/**
 * « Avis », as the tasks and the companies: the reviews the companies left
 * their temp workers, to treat then treated (the tab is in the address), a
 * search, the table and its pages. A review opens its validation modal.
 */
const VivierRatingTempWorker = () => {
  const { text } = useText('reviews');
  const { setObjID, reviewUntreatedDelete } = UseContext();
  const { toggle } = useModal();
  const { toggleSlideOver } = useSlideOver();
  const { pathname, search: params } = useLocation();
  const navigate = useNavigate();
  const tab = pathname.includes('rating-treated') ? 'treated' : 'untreated';
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [untreatedCount, setUntreatedCount] = useState(undefined);
  const selectedId = new URLSearchParams(params).get('idRating');
  const { leave, isLeaving, onLeaveEnd } = useLeavingItems();
  const latest = useRef(0);

  // The search waits for the typing to pause.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  // Only the latest answer counts: a slow one must not overwrite the tab
  // picked since.
  const load = useCallback(() => {
    const request = latest.current + 1;
    latest.current = request;
    setBusy(true);
    setFailed(false);
    const read = tab === 'treated' ? ApiDatabase.getRatingTreatedTempWorkers : ApiDatabase.getRatingUntreatedTempWorkers;
    read(
      { token: localStorage.getItem('xsrfToken'), search, limit: REVIEWS_PER_PAGE, page },
      (answer) => {
        if (request !== latest.current) return;
        setData({ reviews: answer.reviews ?? [], pages: answer.countPages, perPage: answer.countPerPage, count: answer.count });
        if (tab === 'untreated') setUntreatedCount(answer.count);
        setBusy(false);
      },
      (error) => {
        if (request !== latest.current || error?.name === 'CanceledError') return;
        setFailed(true);
        setBusy(false);
      },
    );
  }, [tab, search, page]);
  useEffect(load, [load]);

  // A review treated in its modal leaves the list to treat. The context
  // keeps the last one treated: on a remount it must not take that review
  // out of a list read afterwards (the treated tab lists it).
  useEffect(() => {
    if (!reviewUntreatedDelete || !data?.reviews.some((review) => review._id === reviewUntreatedDelete)) return;
    leave(reviewUntreatedDelete, () => {
      setData((current) => ({ ...current, reviews: current.reviews.filter((review) => review._id !== reviewUntreatedDelete) }));
      setUntreatedCount((count) => (count ? count - 1 : count));
    });
  }, [reviewUntreatedDelete]);

  const pickTab = (next) => {
    if (next === tab) return;
    setData(null);
    setPage(1);
    navigate(PATHS[next]);
  };
  // The review stays named in the address, for its row to stay lit.
  const openReview = (id) => {
    setObjID(id);
    toggle('rating_temp_worker_validation');
    navigate(`${pathname}?idRating=${id}`, { replace: true });
  };
  const openWorker = (id) => {
    setObjID(id);
    toggleSlideOver('get_temp_worker');
  };
  const openCompany = (id) => navigate(`/company/${id}`);

  const reviews = data?.reviews ?? null;
  return (
    <PageForm>
      <div className="flex min-h-0 flex-1 flex-col">
        <ReviewsToolbar tab={tab} untreatedCount={untreatedCount} query={query} onTab={pickTab} onQuery={setQuery} />
        <main className="flex min-h-0 flex-1 flex-col border-t border-gray-100">
          {failed && <CompactEmptyState title={text('loadError')} action={{ label: text('retry'), onClick: load }} />}
          {!failed && reviews?.length === 0 && (
            <CompactEmptyState glyph="star" title={text(`emptyTitle_${tab}`)} description={text(`emptyText_${tab}`)} />
          )}
          {!failed && (!reviews || reviews.length > 0) && (
            <ReviewTable
              reviews={reviews}
              untreated={tab === 'untreated'}
              busy={busy && Boolean(reviews)}
              selectedId={selectedId}
              isLeaving={isLeaving}
              onLeaveEnd={onLeaveEnd}
              onOpen={openReview}
              onOpenWorker={openWorker}
              onOpenCompany={openCompany}
            />
          )}
        </main>
        {!failed && data && data.pages > 1 && (
          <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 py-4 md:px-8">
            <Pagination
              currentPage={page}
              countPerPage={data.perPage}
              countTotal={data.count}
              totalPages={data.pages}
              setCurrentPage={setPage}
            />
          </div>
        )}
      </div>
    </PageForm>
  );
};

export default VivierRatingTempWorker;
